import { useCallback, useEffect, useState } from 'react';
import api from '../../hooks/useApi';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import { LineIcon } from '../shared/LineIcon';
import { HEADER_LINK } from '../shared/CardParts';

// ─── My Time on the home page ────────────────────────────────────────────────
//
// Booked time, as everybody sees it on the way in: this week and the three
// before it day by day, the days not booked while they can still be fixed, and
// when the open week closes. The server works all of that out from the same
// rules as the Monday chase email and the week lock (src/lib/myTime.js), so
// what this says and what the email says cannot drift apart.
//
// Below it, a few figures about the person's time here. They are there to be
// enjoyed rather than acted on, so they sit after the part that needs doing.

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

// A small copy of a grid cell, for the legend under it.
const LEGEND_CHIP = 'inline-flex items-center justify-center w-4 h-3.5 mr-1 rounded border text-[9px] font-bold leading-none';

const REASON = {
  bank_holiday: 'Bank holiday',
  closure: 'Company closure',
  leave: 'On leave',
  half_leave: 'Half day leave',
  non_working: 'Not a working day',
  not_employed: 'Before you started',
};

function fmtDay(d, opts = { weekday: 'short', day: 'numeric', month: 'short' }) {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return new Date(+y, +m - 1, +day).toLocaleDateString('en-GB', opts);
}

const h = (n) => `${Math.round((n || 0) * 10) / 10}h`;
const num = (n) => Number(n || 0).toLocaleString('en-GB');
const plural = (n, word, many = `${word}s`) => `${num(n)} ${n === 1 ? word : many}`;

function DayCell({ d }) {
  const off = d.expected <= 0;
  let cls = 'bg-gray-50 text-gray-400 border-gray-200';                 // nothing expected
  if (d.missing) cls = 'bg-red-50 text-red-600 border-red-300';
  else if (!off && d.when === 'past' && d.logged + 1e-6 >= d.expected) cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  else if (!off && d.when === 'past') cls = 'bg-amber-50 text-amber-700 border-amber-200';
  else if (d.logged > 0) cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  else if (!off) cls = 'bg-white text-gray-400 border-gray-200 border-dashed';
  const title = [
    fmtDay(d.date, { weekday: 'long', day: 'numeric', month: 'long' }),
    off ? (REASON[d.reason] || 'Nothing expected') : `${h(d.logged)} of ${h(d.expected)}`,
    d.missing ? 'Not booked' : '',
  ].filter(Boolean).join(' · ');
  return (
    <div title={title}
      className={`day flex-1 min-w-0 border rounded text-center py-1.5 ${cls} ${d.when === 'today' ? 'ring-2 ring-gold ring-offset-1' : ''}`}>
      <div className="text-[9px] font-bold uppercase opacity-80">{DOW[(new Date(`${d.date}T00:00:00Z`).getUTCDay() + 6) % 7]}</div>
      <div className="text-[11px] font-semibold leading-tight">
        {d.logged > 0 ? Math.round(d.logged * 10) / 10 : off ? '·' : d.missing ? '!' : '–'}
      </div>
    </div>
  );
}

function WeekRow({ w, label, today }) {
  const status = w.locked ? <span className="text-gray-500">Closed</span>
    : w.week_end >= today ? <span className="text-gray-500">Open</span>
    : <span className={w.short ? 'text-amber-700 font-semibold' : 'text-gray-500'}>Closes {w.closes_on === today ? 'tonight' : fmtDay(w.closes_on)}</span>;
  const pct = w.expected > 0 ? Math.min(100, Math.round((w.logged / w.expected) * 100)) : 100;
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 flex-shrink-0 text-[11px] font-semibold text-navy truncate" title={`Week commencing ${fmtDay(w.week_start)}`}>{label}</div>
      <div className="flex gap-1 flex-1 min-w-0 max-w-xs">{w.days.map(d => <DayCell key={d.date} d={d} />)}</div>
      <div className="w-24 flex-shrink-0">
        <div className="text-[11px] text-gray-700 text-right"><span className="font-bold">{h(w.logged)}</span> of {h(w.expected)}</div>
        <div className="h-1 bg-gray-200 rounded-full overflow-hidden mt-0.5">
          <div className={`h-full ${w.short && !w.locked ? 'bg-amber-400' : 'bg-gold'}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="w-24 flex-shrink-0 text-[10px] text-right hidden sm:block">{status}</div>
    </div>
  );
}

// One figure per line: the number, then what it means, so the card is a short
// list that sits level with My Time rather than a tall grid of tiles.
function Stat({ value, label, sub }) {
  return (
    <div className="flex items-baseline gap-3 px-3 py-1.5 min-w-0">
      <div className="w-24 flex-shrink-0 text-[14px] font-extrabold text-navy leading-tight truncate">{value}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] text-gray-600 leading-snug">{label}</div>
        {sub && <div className="text-[10px] font-semibold text-gold truncate">{sub}</div>}
      </div>
    </div>
  );
}

export default function MyTimePanel({ onNavigate, compact = false }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(() => {
    api.get('my-time-overview')
      .then(d => { setData(d); setFailed(false); })
      .catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRefreshOnFocus(load);

  // The stats card is for enjoying, and not everybody wants "Day 1,356" every
  // morning, so it can be hidden. The choice is saved against the login
  // (people/me/views/dashboard), so it follows the person between machines
  // rather than living in one browser.
  const [view, setView] = useState({});
  useEffect(() => {
    api.get('people/me/views/dashboard').then(r => setView(r?.view || {})).catch(() => {});
  }, []);
  const statsHidden = (view.hidden || []).includes('stats');
  const setStatsHidden = (hide) => {
    const hidden = new Set(view.hidden || []);
    if (hide) hidden.add('stats'); else hidden.delete('stats');
    const next = { ...view, hidden: [...hidden] };
    setView(next);
    api.put('people/me/views/dashboard', { view: next }).catch(() => {});
  };

  const go = () => onNavigate && onNavigate('my-time');

  if (failed && !data) return null;
  if (!data) return <div className={`${compact ? '' : 'mb-6'} h-24 bg-gray-100 rounded-md animate-pulse`} />;

  const { today, tracked, weeks = [], missing = [], urgent, streak = 0, stats = {} } = data;
  const thisWeek = weeks[0];
  const missingThisWeek = missing.filter(m => m.date >= thisWeek?.week_start);

  // ── What needs doing, if anything ──
  // The open week closing soonest leads, because it has a deadline. Days this
  // week come next. When nothing is outstanding it says so, briefly.
  let alert = null;
  if (tracked && urgent) {
    const tonight = urgent.closes_today;
    const urgentMissing = missing.filter(m => m.date >= urgent.week_start && m.date < thisWeek.week_start);
    alert = (
      <div className={`flex items-start gap-2 rounded-md px-3 py-2 border ${tonight ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
        <LineIcon name="clock" className={`w-4 h-4 mt-px ${tonight ? 'text-red-700' : 'text-amber-800'}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-[12px] font-bold ${tonight ? 'text-red-700' : 'text-amber-800'}`}>
            {fmtDay(urgent.week_start, { day: 'numeric', month: 'short' })} to {fmtDay(urgent.week_end, { day: 'numeric', month: 'short' })} is {h(urgent.short_by)} short
          </div>
          <div className={`text-[11px] ${tonight ? 'text-red-700' : 'text-amber-800'}`}>
            {tonight
              ? 'It closes at the end of today. After that only an admin, HR or PMO can change it.'
              : `It closes at the end of ${fmtDay(urgent.closes_on, { weekday: 'long', day: 'numeric', month: 'long' })}.`}
            {urgentMissing.length > 0 && ` Not booked: ${urgentMissing.map(m => fmtDay(m.date)).join(', ')}.`}
            {missingThisWeek.length > 0 && ` This week, ${missingThisWeek.map(m => fmtDay(m.date)).join(', ')} ${missingThisWeek.length === 1 ? 'is' : 'are'} not booked yet either.`}
          </div>
        </div>
        <button type="button" onClick={go}
          className={`flex-shrink-0 px-3 py-1.5 rounded text-[11px] font-semibold text-white ${tonight ? 'bg-red-600 hover:bg-red-700' : 'bg-navy hover:bg-navy/90'}`}>
          Book it now
        </button>
      </div>
    );
  } else if (tracked && missingThisWeek.length > 0) {
    alert = (
      <div className="flex items-start gap-2 rounded-md px-3 py-2 border bg-amber-50 border-amber-200">
        <LineIcon name="clock" className="w-4 h-4 mt-px text-amber-800" />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-bold text-amber-800">
            {plural(missingThisWeek.length, 'day')} this week not booked yet
          </div>
          <div className="text-[11px] text-amber-800">{missingThisWeek.map(m => fmtDay(m.date)).join(', ')}. Booking as you go keeps it quick.</div>
        </div>
        <button type="button" onClick={go} className="flex-shrink-0 px-3 py-1.5 rounded text-[11px] font-semibold text-white bg-navy hover:bg-navy/90">
          Book time
        </button>
      </div>
    );
  } else if (tracked) {
    alert = (
      <div className="text-[11px] text-emerald-700 font-semibold">
        ✓ All booked up to yesterday{streak >= 5 ? `: ${plural(streak, 'working day')} in a row` : ''}.
      </div>
    );
  }

  const labels = ['This week', 'Last week'];

  if (compact) {
    return (
      <div className="bg-white border border-gray-200 rounded-md p-3 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-[12px] font-bold text-navy">My Time</h3>
          <button type="button" onClick={go} className={HEADER_LINK}>Open My Time</button>
        </div>
        {alert}
        {thisWeek && <WeekRow w={thisWeek} label="This week" today={today} />}
      </div>
    );
  }

  // ── The figures about their time here ──
  // Each one shows only when there is something true to say.
  const ann = stats.anniversary;
  const chips = [
    stats.days_here && {
      key: 'here', value: `Day ${num(stats.days_here)}`, label: 'at Alton Valley',
      sub: ann && (ann.in_days === 0
        ? `Happy ${ann.years}-year anniversary! 🎉`
        : `${ann.years} year${ann.years === 1 ? '' : 's'} on ${fmtDay(ann.date, { day: 'numeric', month: 'short' })}, in ${plural(ann.in_days, 'day')}`),
    },
    stats.hours_total > 0 && {
      key: 'hours', value: num(Math.round(stats.hours_total)) + 'h', label: 'booked in total',
      sub: `About ${plural(Math.round(stats.hours_total / 7.5), 'working day')}`,
    },
    stats.sows > 0 && {
      key: 'sows', value: plural(stats.sows, 'SOW'), label: stats.clients > 0 ? `for ${plural(stats.clients, 'client')}` : 'booked to',
    },
    stats.top_sow && {
      key: 'top', value: stats.top_sow.reference, label: `your most-booked SOW, ${h(stats.top_sow.hours)}`,
      sub: stats.top_sow.client_name || stats.top_sow.detail || null,
    },
    // A run of one or two is not much of a run; it appears from three.
    streak >= 3 && tracked && {
      key: 'streak', value: <span className="inline-flex items-center gap-1">{num(streak)}<LineIcon name="flame" className="w-4 h-4 text-gold" /></span>, label: `working day${streak === 1 ? '' : 's'} in a row with time booked`,
    },
    stats.busiest_weekday && {
      key: 'busy', value: stats.busiest_weekday, label: 'your busiest day over the last year',
    },
    stats.favourite_desk && {
      key: 'desk', value: stats.favourite_desk.desk, label: `your favourite desk, booked ${plural(stats.favourite_desk.n, 'time')}`,
      sub: stats.favourite_desk.location || null,
    },
  ].filter(Boolean);

  return (
    <div className="mb-6 grid grid-cols-1 xl:grid-cols-5 gap-3">
      <div className={`bg-white border border-gray-200 rounded-md min-w-0 flex flex-col ${statsHidden ? 'xl:col-span-5' : 'xl:col-span-3'}`}>
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-[12px] font-bold text-navy">My Time</h3>
          <div className="flex items-center gap-3">
            {statsHidden && (
              <button type="button" onClick={() => setStatsHidden(false)} className="text-[11px] font-semibold text-gray-500 hover:text-gold hover:underline">
                Show my stats
              </button>
            )}
            <button type="button" onClick={go} className={HEADER_LINK}>Open My Time</button>
          </div>
        </div>
        <div className="p-3 flex-1 flex flex-col gap-3">
          {alert}
          {!tracked && (
            <div className="text-[12px] text-gray-600">Your time is not tracked against expected hours, so nothing here is ever chased.</div>
          )}
          <div className="space-y-2">
            {weeks.map((w, i) => (
              <WeekRow key={w.week_start} w={w} today={today}
                label={labels[i] || `w/c ${fmtDay(w.week_start, { day: 'numeric', month: 'short' })}`} />
            ))}
          </div>
          <div className="mt-auto flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-gray-600">
            {/* Each key is drawn the way its cell looks in the grid above. */}
            <span className="inline-flex items-center"><span className={LEGEND_CHIP + ' bg-emerald-50 text-emerald-700 border-emerald-200'} />Booked</span>
            <span className="inline-flex items-center"><span className={LEGEND_CHIP + ' bg-amber-50 text-amber-700 border-amber-200'} />Part booked</span>
            <span className="inline-flex items-center"><span className={LEGEND_CHIP + ' bg-red-50 text-red-600 border-red-300'}>!</span>Not booked</span>
            <span className="inline-flex items-center"><span className={LEGEND_CHIP + ' day bg-gray-50 text-gray-400 border-gray-200'}>·</span>Nothing expected (hover for why)</span>
            <span className="inline-flex items-center"><span className={LEGEND_CHIP + ' bg-white text-gray-400 border-gray-200 border-dashed'}>–</span>Still to come</span>
          </div>
        </div>
      </div>

      {!statsHidden && (
      <div className="bg-white border border-gray-200 rounded-md xl:col-span-2 flex flex-col">
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-[12px] font-bold text-navy">You at Alton Valley</h3>
          <button type="button" onClick={() => setStatsHidden(true)} title="Hide this card. Show my stats on My Time brings it back."
            className="text-[11px] font-semibold text-gray-500 hover:text-gold hover:underline">Hide</button>
        </div>
        {chips.length === 0 ? (
          <div className="p-4 text-[12px] text-gray-500">Book some time and your numbers will start to appear here.</div>
        ) : (
          <div className="py-1.5 flex-1 divide-y divide-gray-100">
            {chips.map(c => <Stat key={c.key} value={c.value} label={c.label} sub={c.sub} />)}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
